import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/UTDT_Logo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

// Register Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export default function ConstructionTable11({ data = [] }) {
  const pdfRef = useRef();

  if (!Array.isArray(data) || data.length === 0) {
    return <p>No data available</p>;
  }

  // Extract chart values
  const chartData = {
    labels: data.map((d) => d.year),
    datasets: [
      {
        label: "Female Employment Count (14–18)",
        data: data.map((d) => parseInt(d.emp_count || 0)),
        backgroundColor: "#EC4899", // purple
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "(14–18) Age Group Female Employment Trend (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.parsed.y.toLocaleString("en-US")}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Year" },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Employment Count" },
        ticks: {
          callback: function (value) {
            return value.toLocaleString("en-US");
          },
        },
      },
    },
  };

  // Summary Insights
  const firstYear = parseInt(data[0]?.emp_count || 0);
  const lastYear = parseInt(data[data.length - 1]?.emp_count || 0);
  const growthPercent = firstYear
    ? (((lastYear - firstYear) / firstYear) * 100).toFixed(1)
    : 0;

  const topYear = data.reduce(
    (max, item) => (item.emp_count > max.emp_count ? item : max),
    data[0],
  );

  // PDF Download
const downloadPDF = () => {
  const input = pdfRef.current;
  html2canvas(input, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    scrollY: -window.scrollY,
  }).then((canvas) => {
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth - 10;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;
    let pageNumber = 1;

    const addHeader = () => {
      const logoWidth = 20; // ideal header width
      const logoHeight = 20; // adjust ratio if needed

      pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
      pdf.setFontSize(26);
      pdf.text("UTahData.org", 95, 22);
      pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
      pdf.line(10, 35, 200, 35);
    };

    const addFooter = (pageNumber) => {
      pdf.setFontSize(9);
      const footerY = pageHeight - 10;
      pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        10,
        footerY,
      );
      pdf.text(
        `Page ${pageNumber}`,
        pageWidth - 10,
        footerY,
        null,
        null,
        "right",
      );
    };

    // First page
    addHeader();
    pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
    heightLeft -= pageHeight - 32;

    // Add more pages if needed
    while (heightLeft > 0) {
      pdf.addPage();
      pageNumber++;
      addHeader();
      position = heightLeft - imgHeight;
      pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;
      addFooter(pageNumber);
    }

    // Final footer
    // ===== FINAL FOOTER SAFE AREA =====

    const footerTopY = pageHeight - 40; // start footer 40mm from bottom
    const footerContentY = pageHeight - 28; // logos row
    const footerBottomTextY = pageHeight - 10; // bottom text row

    // Top Divider Line
    pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

    // UtahData logo
    const utahWidth = 18;
    const utahHeight = 18;
    pdf.addImage(
      utahData,
      "PNG",
      20,
      footerContentY - 8,
      utahWidth,
      utahHeight,
    );

    // Built by
    pdf.setFontSize(10);
    pdf.text("Built by", 45, footerContentY);

    // BODEX logo
    const bodexWidth = 36;
    const bodexHeight = bodexWidth * 0.3;
    pdf.addImage(bodex, "PNG", 60, footerContentY - 6, bodexWidth, bodexHeight);

    // Using text
    pdf.text("using", 102, footerContentY);

    // KPI logo
    pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

    // Version
    pdf.text("20250916.v3.198", 140, footerContentY);

    // Bottom Info Line
    pdf.setFontSize(8);
    pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
    pdf.text(
      `Downloaded on: ${new Date().toLocaleDateString()}`,
      pageWidth - 10,
      footerBottomTextY,
      null,
      null,
      "right",
    );

    pdf.save("female-age-14-18-employment.pdf");
  });
};

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-purple-50 via-white to-blue-50 rounded-xl shadow-md"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          (14–18) Age Group Female Employment Trend (2018–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-pink-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Overall Growth (2018–2024)
            </p>
            <p className="text-xl font-bold text-pink-800">+{growthPercent}%</p>
          </div>
          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Peak Year</p>
            <p className="text-xl font-bold text-yellow-800">{topYear.year}</p>
          </div>
          <div className="p-4 bg-purple-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Latest Employment (2024)
            </p>
            <p className="text-xl font-bold text-purple-800">
              {lastYear.toLocaleString("en-US")} 👩‍💼
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col h-full">
            <div className="overflow-x-auto flex-grow">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Year</th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Employment Count
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr
                      key={row.year}
                      className={`${
                        row.year === topYear.year
                          ? "bg-yellow-50 font-bold"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <td className="p-2 text-left">{row.year}</td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {parseInt(row.emp_count || 0).toLocaleString("en-US")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
